import { IndexedDbStorageAdapter } from "@tattoo-stencil/storage";
import { isAiStyle, type AiStyle } from "./aiTypes";

const CACHE_KEY = "pending-stencil-intake";
const MAX_AGE_MS = 60 * 60 * 1000;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const INTAKE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u;

type StoredPendingStencilIntake = Readonly<{
  version: 1;
  id: string;
  fileName: string;
  fileType: string;
  lastModified: number;
  bytes: Uint8Array;
  style: AiStyle;
  createdAt: number;
}>;

export type PendingStencilIntake = Readonly<{
  id: string;
  file: File;
  style: AiStyle;
}>;

export function isPendingStencilIntakeId(value: unknown): value is string {
  return typeof value === "string" && INTAKE_ID.test(value);
}

export function pendingStencilDestination(id: string): string {
  if (!isPendingStencilIntakeId(id)) throw new Error("The pending stencil reference is invalid.");
  return `/ai?intake=${id}`;
}

async function withCache<T>(operation: (adapter: IndexedDbStorageAdapter) => Promise<T>): Promise<T> {
  const adapter = await IndexedDbStorageAdapter.open();
  try { return await operation(adapter); }
  finally { adapter.close(); }
}

export async function savePendingStencilIntake(file: File, style: AiStyle): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  if (bytes.byteLength === 0 || bytes.byteLength > MAX_FILE_BYTES) throw new Error("Choose a PNG or JPEG smaller than 10 MB.");
  const id = crypto.randomUUID();
  const record: StoredPendingStencilIntake = {
    version: 1,
    id,
    fileName: file.name.slice(0, 180) || "reference-image",
    fileType: file.type,
    lastModified: file.lastModified,
    bytes,
    style,
    createdAt: Date.now(),
  };
  await withCache(adapter => adapter.transaction(["disposable-cache"], "readwrite", transaction =>
    transaction.put("disposable-cache", CACHE_KEY, record)));
  return id;
}

function validRecord(value: unknown): value is StoredPendingStencilIntake {
  if (!value || typeof value !== "object") return false;
  const record = value as Partial<StoredPendingStencilIntake>;
  return record.version === 1
    && isPendingStencilIntakeId(record.id)
    && typeof record.fileName === "string"
    && record.fileName.length > 0
    && record.fileName.length <= 180
    && typeof record.fileType === "string"
    && typeof record.lastModified === "number"
    && record.bytes instanceof Uint8Array
    && record.bytes.byteLength > 0
    && record.bytes.byteLength <= MAX_FILE_BYTES
    && isAiStyle(record.style)
    && typeof record.createdAt === "number"
    && Date.now() - record.createdAt >= 0
    && Date.now() - record.createdAt <= MAX_AGE_MS;
}

export async function readPendingStencilIntake(id: string): Promise<PendingStencilIntake | null> {
  if (!isPendingStencilIntakeId(id)) return null;
  return withCache(async adapter => {
    const value = await adapter.transaction(["disposable-cache"], "readonly", transaction =>
      transaction.get<StoredPendingStencilIntake>("disposable-cache", CACHE_KEY));
    if (!validRecord(value) || value.id !== id) {
      if (value !== undefined && (!validRecord(value) || value.id === id)) {
        await adapter.transaction(["disposable-cache"], "readwrite", transaction => transaction.delete("disposable-cache", CACHE_KEY));
      }
      return null;
    }
    const fileBytes = new Uint8Array(value.bytes.byteLength);
    fileBytes.set(value.bytes);
    return {
      id,
      file: new File([fileBytes.buffer], value.fileName, { type: value.fileType, lastModified: value.lastModified }),
      style: value.style,
    };
  });
}

export async function deletePendingStencilIntake(id: string): Promise<void> {
  if (!isPendingStencilIntakeId(id)) return;
  await withCache(adapter => adapter.transaction(["disposable-cache"], "readwrite", async transaction => {
    const value = await transaction.get<StoredPendingStencilIntake>("disposable-cache", CACHE_KEY);
    if (validRecord(value) && value.id === id) await transaction.delete("disposable-cache", CACHE_KEY);
  }));
}
